import api from './client'
import { APP_BASE } from '@/config/basePath'
import type { DiscoveryCatalog, ScopeDecisions } from '@/types'

export interface GenerateJsonPayload {
  description: string
  suggestEnhancements?: boolean
  enhanceScope?: boolean
  selfReview?: boolean
  reviewCount?: number
  referencedProjects?: Array<{ folder_name: string; owner_user_id: string }>
  attachments?: import('@/lib/attachments').WireAttachment[]
  scopeDecisions?: import('@/types').ScopeDecisions
}

export async function generateJson(data: GenerateJsonPayload) {
  const res = await api.post('/generateapp/generate-json', data)
  return res.data
}

export interface OnboardingHelpTurn {
  role: 'user' | 'assistant'
  content: string
}

/** Ask the onboarding assistant a free-text question about Twasta features.
 *  Uses the platform-default AI (within the user's allowance); a 402 here is
 *  handled by the global AiAccessModal. */
export async function onboardingHelp(
  question: string,
  history: OnboardingHelpTurn[] = [],
): Promise<string> {
  const res = await api.post('/generateapp/onboarding-help', { question, history })
  return res.data.answer as string
}

export async function getJson(project: string = 'Draft Application') {
  const res = await api.get('/generateapp/json', { params: { project } })
  return res.data
}

/** Get raw application JSON with flat navigation (no backend transformation) */
export async function getJsonRaw(project: string = 'Draft Application') {
  const res = await api.get('/generateapp/json', { params: { project, raw: true } })
  return res.data
}

export async function saveJson(project: string, appData: unknown) {
  const res = await api.post('/generateapp/save-json', { project, appData })
  return res.data
}

export async function getFrsContent(project?: string) {
  const url = project
    ? `/generateapp/frs-content/${encodeURIComponent(project)}`
    : '/generateapp/frs-content'
  const res = await api.get(url)
  return res.data
}

export async function saveFrsContent(
  modified_frs: string,
  project: string = 'Draft Application'
) {
  const res = await api.post('/generateapp/save-frs-content', { modified_frs, project })
  return res.data
}

export async function generateFrs(data: Record<string, unknown>) {
  const res = await api.post('/generateapp/generate-frs', data)
  return res.data
}

export async function proposeScope(data: Record<string, unknown>) {
  const res = await api.post('/generateapp/propose-scope', data)
  return res.data
}

/** The "Help me decide" catalogue (industries, processes, solution types). */
export async function getDiscoveryCatalog(): Promise<DiscoveryCatalog> {
  const res = await api.get('/generateapp/discovery-catalog')
  return res.data
}

/** Render confirmed scope decisions into the CONFIRMED SCOPE block a build
 *  brief carries (the build then skips its own scope round). */
export async function getScopeBlock(decisions: ScopeDecisions): Promise<string> {
  const res = await api.post('/generateapp/scope-block', { decisions })
  return res.data.block as string
}

/** Make "Draft Application" access-resolvable so pre-FRS attachments (a
 *  database connection, a git/zip reference source) can persist into it.
 *  Idempotent. */
export async function prepareDraft(): Promise<void> {
  await api.post('/generateapp/prepare-draft')
}

/** Drop a leftover "Draft Application" husk (the empty marker /prepare-draft
 *  leaves behind once its sources have been adopted into a named project).
 *  `blocking` is true only when the draft holds real work. */
export async function discardEmptyDraft(): Promise<{ removed: boolean; blocking: boolean }> {
  const { data } = await api.post('/generateapp/discard-empty-draft')
  return data
}

/** Move sources staged on "Draft Application" (connections, git/zip reference
 *  sources) into a just-created named project so the build can reach them. */
export async function adoptDraftSources(
  project: string,
): Promise<{ connections: number; reference_sources: number }> {
  const { data } = await api.post('/generateapp/adopt-draft-sources', { project })
  return data
}

export async function generateFrsEnhanced(data: GenerateJsonPayload) {
  const res = await api.post('/generateapp/generate-frs-enhanced', data)
  return res.data
}

export async function generateAppFromFrs(
  modified_frs: string,
  project: string = 'Draft Application'
) {
  const res = await api.post('/generateapp/generate-app-from-frs', { modified_frs, project })
  return res.data
}

export async function generateModel(data: {
  obj_name: string
  obj_type: string
  description: string
  functional_specs: string
  project_folder: string
}) {
  const res = await api.post('/generateapp/generate-model', data)
  return res.data
}

export async function generateDatabase(data: Record<string, unknown>) {
  const res = await api.post('/generateapp/generate-database', data)
  return res.data
}

export async function deleteDatabase(data: Record<string, unknown>) {
  const res = await api.post('/generateapp/delete-database', data)
  return res.data
}

export function downloadModel(project: string, objType: string, objName: string): string {
  return `${APP_BASE}/api/generateapp/download-model/${encodeURIComponent(project)}/${encodeURIComponent(objType)}/${encodeURIComponent(objName)}`
}

export async function generatePrototype(data: {
  obj_name: string
  project: string
  obj_type: string
}) {
  const res = await api.post('/generateapp/generate-prototype', {
    obj_name: data.obj_name,
    project_folder: data.project,
    obj_type: data.obj_type,
  })
  return res.data
}

export async function showPrototype(data: {
  obj_name: string
  project: string
  obj_type: string
}) {
  const res = await api.post('/generateapp/show-prototype', data)
  return res.data
}

export async function checkModelStatus(
  project: string,
  objName: string,
  objType: string
) {
  const res = await api.get(
    `/generateapp/check-model-status/${encodeURIComponent(project)}/${encodeURIComponent(objName)}/${encodeURIComponent(objType)}`
  )
  return res.data
}

export async function checkPrototypeStatus(
  project: string,
  objName: string,
  objType: string
) {
  const res = await api.get(
    `/generateapp/check-prototype-status/${encodeURIComponent(project)}/${encodeURIComponent(objName)}/${encodeURIComponent(objType)}`
  )
  return res.data
}

export async function checkBatchModelStatus(
  project: string,
  objects: Array<{ obj_name: string; obj_type: string }>
): Promise<{ statuses: Record<string, { model_exists: boolean }> }> {
  const res = await api.post('/generateapp/batch-model-status', { project, objects })
  return res.data
}

export async function enhanceSpecification(data: Record<string, unknown>) {
  const res = await api.post('/generateapp/enhance-specification', data)
  return res.data
}

export async function enhanceFrsWithAi(data: Record<string, unknown>) {
  const res = await api.post('/generateapp/enhance-frs-with-ai', data)
  return res.data
}

export async function enhanceDescription(description: string): Promise<string> {
  const res = await api.post('/generateapp/enhance-description', { description })
  return res.data.description
}
